export const hasWindowSupport = typeof window !== 'undefined';
export const hasDocumentSupport = typeof document !== 'undefined';
export const hasNavigatorSupport = typeof navigator !== 'undefined';

export const isBrowser = hasWindowSupport && hasDocumentSupport && hasNavigatorSupport;

export const userAgent = isBrowser ? window.navigator.userAgent.toLowerCase() : '';

export const isIE = /msie|trident/.test(userAgent);

export const hasIntersectionObserverSupport =
  isBrowser &&
  'IntersectionObserver' in window &&
  'IntersectionObserverEntry' in window &&
  // Edge 15 and UC Browser lack support for `isIntersecting`
  // but we an use intersectionRatio > 0 instead
  // 'isIntersecting' in window.IntersectionObserverEntry.prototype &&
  'intersectionRatio' in window.IntersectionObserverEntry.prototype;


export function isValidColor(val) {
  return (new Option().style.color = val) === val;
}

export function randomStr() {
  return (Math.floor(Math.random() * 100000) * Date.now()).toString(16)
}

export function isValidSize(val) {
  return !isNaN(val) || ['rem', 'px', 'em', '%'].some(suffix => val.endsWith(suffix))
}

export function objectMerge(obj, src) {
  Object.keys(src).forEach(function (key) { obj[key] = src[key]; });
  return obj;
}

export function convertToUnit(str, unit = 'px') {
  if (str == null || str === '') {
    return undefined
  } else if (isNaN(+str)) {
    return String(str)
  } else {
    return `${Number(str)}${unit}`
  }
}

export function kebabCase(str) {
  return (str || '').replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

export const camelize = (str) => {
  const camelizeRE = /-(\w)/g;
  return str.replace(camelizeRE, (_, c) => c ? c.toUpperCase() : '')
};

// KeyboardEvent.keyCode aliases
export const keyCodes = Object.freeze({
  enter: 13,
  tab: 9,
  delete: 46,
  esc: 27,
  space: 32,
  up: 38,
  down: 40,
  left: 37,
  right: 39,
  end: 35,
  home: 36,
  del: 46,
  backspace: 8,
  insert: 45,
  pageup: 33,
  pagedown: 34
});

export function arrayDiff(a, b) {
  const diff = [];
  for (let i = 0; i < b.length; i++) {
    if (a.indexOf(b[i]) < 0) diff.push(b[i])
  }
  return diff
}

/**
 * Makes the first character of a string uppercase
 */
export function upperFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function formatParams(queryName, value) {
  queryName = queryName.replace(/=/g, '');
  let result = [];

  switch (value.constructor) {
    case String:
    case Number:
    case Boolean:
      result.push(encodeURIComponent(queryName) + '=' + encodeURIComponent(value));
      break;

    case Array:
      value.forEach(function (item) {
        result = result.concat(formatParams(queryName + '[]=', item))
      });
      break;

    case Object:
      Object.keys(value).forEach(function (key) {
        let item = value[key];
        result = result.concat(formatParams(queryName + '[' + key + ']', item))
      });
      break
  }

  return result
}

export function flatten(array) {
  let queries = [];
  array.forEach(function (item) {
    if (typeof item === 'string') {
      queries.push(item)
    } else {
      queries = queries.concat(flatten(item))
    }
  });
  return queries
}

export function createSlug(str) {
  str = str.replace(/^\s+|\s+$/g, ""); // trim
  str = str.toLowerCase();

  // remove accents, swap ñ for n, etc
  const from = "åàáãäâèéëêìíïîòóöôùúüûñç·/_,:;";
  const to = "aaaaaaeeeeiiiioooouuuunc------";

  let i = 0, l = from.length;
  for (; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
  }

  str = str
    .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
    .replace(/\s+/g, "-") // collapse whitespace and replace by -
    .replace(/-+/g, "-") // collapse dashes
    .replace(/^-+/, "") // trim - from start of text
    .replace(/-+$/, ""); // trim - from end of text

  return str;
}

export function isString(value) {
  return typeof value === 'string' || value instanceof String;
}

export function isNumber(value) {
  return typeof value === 'number' && isFinite(value);
}

export function isArray(value) {
  return value && typeof value === 'object' && value.constructor === Array;
}

export function isFunction(value) {
  return typeof value === 'function';
}

export function isObject(value) {
  return value && true && typeof value === 'object' && value.constructor === Object;
}

export function isUndefined(value) {
  return typeof value === 'undefined';
}

export function isNull(value) {
  return value === null;
}

export function isBoolean(value) {
  return typeof value === 'boolean';
}

export function isRegExp(value) {
  return value && typeof value === 'object' && value.constructor === RegExp;
}

export function isDate(value) {
  return value instanceof Date;
}

export function isSymbol(value) {
  return typeof value === 'symbol';
}


export function isScriptLoaded(src) {
  return document.querySelectorAll(`script[src="${src}"]`).length > 0;
}

export function loadExternalScript(src, id = null) {
  return new Promise((resolve, reject) => {
    let async_load = function () {
      if (!isScriptLoaded(src)) {
        let first, s;
        s = document.createElement('script');
        s.src = src;
        s.id = id;
        s.type = 'text/javascript';
        s.async = true;
        s.defer = true;
        s.onload = () => {
          resolve()
        };
        s.onerror = () => {
          reject()
        };
        first = document.getElementsByTagName('script')[0];
        return first.parentNode.insertBefore(s, first);
      }
    };

    if (window.attachEvent != null) {
      window.attachEvent('onload', async_load);
    } else {
      window.addEventListener('load', async_load, false);
    }
  });
}

export function loadScript(src) { // eslint-disable-line no-param-reassign
  return new Promise(function (resolve, reject) {
    if (document.querySelector('script[src="' + src + '"]')) {
      resolve();

      return;
    }

    const el = document.createElement('script');

    el.type = 'text/javascript';
    el.async = true;
    el.src = src;

    el.addEventListener('load', resolve);
    el.addEventListener('error', reject);
    el.addEventListener('abort', reject);

    // let first = document.getElementsByTagName('script')[0];
    // return first.parentNode.insertBefore(el, first);

    document.head.appendChild(el);
  });
}

export function unloadScript(src) { // eslint-disable-line no-param-reassign
  return new Promise(function (resolve, reject) {
    const el = document.querySelector('script[src="' + src + '"]');

    if (!el) {
      reject();

      return;
    }

    document.head.removeChild(el);

    resolve();
  });
}
